/**
 * Sheet 07d — password reset (A.5, NF.9; FR-020; research §1.9; callout 9).
 *
 * Request mode: the API answers 202 with identical bytes whether the address
 * exists, is throttled or is unknown, and this screen shows one confirmation
 * sentence for every such answer, validation refusals included, so the browser
 * cannot leak what the API hides. Only an answer that means no token was stored
 * and no mail will go out — the 503 `try-again`, an origin refusal, any 5xx —
 * or a transport failure shows a retry notice instead; a confirmation there
 * would be false.
 *
 * Complete mode: the emailed link is `/reset#token=…`. The token travels in the
 * fragment so it never reaches a server log or a Referer (F15; index.html sets
 * `referrer: no-referrer`). It is read into state once, and the fragment is
 * removed with `history.replaceState` in a layout effect, before the first
 * paint completes, so it survives in neither the address bar nor the history
 * entry. A completed reset ends every session of the identity, so the cached
 * tenant is dropped and the user is sent to log in with their authenticator
 * (MFA survives a reset, callout 10).
 */
import { useId, useLayoutEffect, useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { ApiError } from '@/lib/api'
import { PROBLEM, completeReset, fieldErrors, getSignupPolicy, isProblem, requestReset } from '@/lib/auth'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { clearTenantQuery } from '@/components/trellis/AuthGate'
import { AuthLayout, DisabledReason, FieldError, failureCopy } from './AuthLayout'
import { SIGNUP_POLICY_QUERY_KEY } from './Signup'

export const RESET_REQUESTED_MESSAGE =
  'If that address has an account, a reset link is on its way. It expires in one hour.'
export const RESET_DONE_MESSAGE =
  'Your password is changed. Log in with your new password and your authenticator.'
export const RESET_EXPIRED_MESSAGE = 'This link has expired or was already used.'
const RESET_TRANSPORT_MESSAGE = 'Trellis could not send the request. Check your connection and try again.'

/**
 * Did the server take the request? A 202 and every 4xx validation refusal
 * read as "accepted": the screen must not distinguish what the API
 * deliberately does not. The 503 `try-again`, an origin refusal and any 5xx
 * mean nothing was stored and nothing will be sent.
 */
export function resetRequestAccepted(err: ApiError): boolean {
  const { type, status } = err.problem
  if (type === PROBLEM.TRY_AGAIN || type === PROBLEM.ORIGIN_NOT_ALLOWED) return false
  return status < 500
}

/** `#token=<plaintext>` → the plaintext; anything else → null. */
export function readResetToken(hash: string): string | null {
  const m = /^#token=(.+)$/.exec(hash)
  if (!m?.[1]) return null
  try {
    return decodeURIComponent(m[1])
  } catch {
    return m[1]
  }
}

export default function Reset() {
  // Read once; the fragment is gone by the time anything re-renders.
  const [token] = useState(() => readResetToken(window.location.hash))
  const [mode, setMode] = useState<'request' | 'complete'>(token ? 'complete' : 'request')

  useLayoutEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }, [])

  return mode === 'complete' && token ? (
    <Complete token={token} onRequestAgain={() => setMode('request')} />
  ) : (
    <Request />
  )
}

// ---------------------------------------------------------------------------

function Request() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const emailId = useId()
  const reasonId = useId()

  const reason = email.trim() === '' ? 'Send reset link is unavailable until you enter your email.' : null
  const canSubmit = reason === null && !submitting

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      await requestReset({ email: email.trim() })
      setSent(true)
    } catch (err) {
      if (!(err instanceof ApiError)) setError(RESET_TRANSPORT_MESSAGE)
      else if (resetRequestAccepted(err)) setSent(true)
      else setError(failureCopy(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout
      title="Reset your password"
      lede="Enter the email address for your account. If it has one, a reset link is on its way; it expires in one hour."
    >
      {sent ? (
        <Alert variant="success" role="status">
          <AlertDescription>
            <p>{RESET_REQUESTED_MESSAGE}</p>
          </AlertDescription>
        </Alert>
      ) : (
        <form className="flex flex-col gap-5" onSubmit={(e) => void onSubmit(e)} noValidate>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={emailId}>Email</Label>
            <Input
              id={emailId}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {error && (
            <Alert variant="blocked">
              <AlertDescription>
                <p>{error}</p>
              </AlertDescription>
            </Alert>
          )}
          <div className="flex flex-col gap-2">
            <Button
              type="submit"
              size="lg"
              disabled={!canSubmit}
              aria-busy={submitting || undefined}
              aria-describedby={reason ? reasonId : undefined}
            >
              Send reset link
            </Button>
            {reason && <DisabledReason id={reasonId}>{reason}</DisabledReason>}
          </div>
        </form>
      )}
      <p className="text-body-sm text-navy-600">
        <Link to="/login" className="text-[var(--color-link)] hover:underline">
          Back to log in
        </Link>
      </p>
    </AuthLayout>
  )
}

// ---------------------------------------------------------------------------

function Complete({ token, onRequestAgain }: { token: string; onRequestAgain: () => void }) {
  const queryClient = useQueryClient()
  const policy = useQuery({ queryKey: SIGNUP_POLICY_QUERY_KEY, queryFn: getSignupPolicy, staleTime: 5 * 60_000 })
  const minLength = policy.data?.password_min_length ?? 12

  const [password, setPassword] = useState('')
  const [touched, setTouched] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  const [done, setDone] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const passwordId = useId()
  const reasonId = useId()

  const tooShort = password.length < minLength
  const shownError = fieldError ?? (touched && password && tooShort ? `Use at least ${minLength} characters.` : null)
  const reason = tooShort ? `Change password is unavailable until the password has at least ${minLength} characters.` : null
  const canSubmit = reason === null && !submitting

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    setFieldError(null)
    try {
      await completeReset({ token, password })
      clearTenantQuery(queryClient)
      setPassword('')
      setDone(true)
    } catch (err) {
      if (isProblem(err, PROBLEM.INVALID_OR_EXPIRED_TOKEN)) setExpired(true)
      else if (isProblem(err, PROBLEM.VALIDATION_FAILED)) {
        const pw = fieldErrors(err).find((f) => f.field === 'password')
        if (pw) setFieldError(pw.message)
        else setError(failureCopy(err))
      } else setError(failureCopy(err))
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <AuthLayout title="Password changed">
        <Alert variant="success" role="status">
          <AlertDescription>
            <p>{RESET_DONE_MESSAGE}</p>
          </AlertDescription>
        </Alert>
        <div>
          <Button asChild size="lg">
            <Link to="/login">Log in</Link>
          </Button>
        </div>
      </AuthLayout>
    )
  }

  if (expired) {
    return (
      <AuthLayout title="Choose a new password">
        <Alert variant="blocked">
          <AlertTitle>{RESET_EXPIRED_MESSAGE}</AlertTitle>
          <AlertDescription>
            <p>Reset links work once and expire after one hour. Request a new one to continue.</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={onRequestAgain}>
              Request a new link
            </Button>
          </AlertDescription>
        </Alert>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Choose a new password"
      lede="Changing your password ends every other session of your account. Your authenticator stays as it is."
    >
      <form className="flex flex-col gap-5" onSubmit={(e) => void onSubmit(e)} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={passwordId}>New password</Label>
          <Input
            id={passwordId}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              setFieldError(null)
            }}
            onBlur={() => setTouched(true)}
            aria-invalid={shownError ? true : undefined}
            aria-describedby={[shownError ? `${passwordId}-error` : null, `${passwordId}-help`].filter(Boolean).join(' ')}
          />
          {shownError && <FieldError id={`${passwordId}-error`}>{shownError}</FieldError>}
          <p id={`${passwordId}-help`} className="text-caption text-navy-600">
            At least {minLength} characters.
          </p>
        </div>

        {error && (
          <Alert variant="blocked">
            <AlertDescription>
              <p>{error}</p>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            size="lg"
            disabled={!canSubmit}
            aria-busy={submitting || undefined}
            aria-describedby={reason ? reasonId : undefined}
          >
            Change password
          </Button>
          {reason && <DisabledReason id={reasonId}>{reason}</DisabledReason>}
        </div>
      </form>
    </AuthLayout>
  )
}
